import { inject } from "aurelia-dependency-injection";
import { EventAggregator } from "aurelia-event-aggregator";
import {
  Interceptor,
  RequestMessage,
  HttpResponseMessage,
} from "aurelia-http-client";
import { Buffer } from "buffer";
import { AuthConfig } from "./auth-config";
import { AuthStorage } from "./auth-storage";
import { joinUrl, isObject, isString } from "./auth-utils";

@inject(EventAggregator, AuthStorage, AuthConfig)
export class Authentication {
  private tokenName: string;
  private initialUrl: string;
  private decomposedToken?: any = undefined;

  constructor(
    private eventAggregator: EventAggregator,
    private storage: AuthStorage,
    private config: AuthConfig,
  ) {
    this.tokenName = this.config.tokenPrefix
      ? this.config.tokenPrefix + "_" + this.config.tokenName
      : this.config.tokenName;
  }

  getLoginRoute() {
    return this.config.loginRoute;
  }

  getLoginRedirect() {
    return (
      this.initialUrl || joinUrl(this.config.baseUrl, this.config.loginRedirect)
    );
  }

  getLogoutRedirect() {
    return joinUrl(this.config.baseUrl, this.config.logoutRedirect);
  }

  getLoginUrl() {
    return joinUrl(this.config.baseUrl, this.config.loginUrl);
  }

  getChangePasswordUrl() {
    return joinUrl(this.config.baseUrl, this.config.changePasswordUrl);
  }

  getSignupUrl() {
    return joinUrl(this.config.baseUrl, this.config.signupUrl);
  }

  getNoAccessRoute() {
    return this.config.noAccessRoute;
  }

  getToken() {
    return this.storage.get(this.tokenName);
  }

  getTokenPayload() {
    if (this.decomposedToken === undefined) {
      let token = this.getToken();
      this.decomposedToken = this.decomposeToken(token);
    }
    return this.decomposedToken;
  }

  getTokenAuthHeaderValue(): string | undefined {
    let token = this.getToken();
    return token
      ? this.config.authToken
        ? `${this.config.authToken} ${token}`
        : token
      : undefined;
  }

  hasRightOneOf(rights: string[]): boolean {
    let tokenPayload = this.getTokenPayload();
    let tokenRights = (tokenPayload && tokenPayload.rights) || [];
    return tokenRights.filter((r) => rights.indexOf(r) >= 0).length > 0;
  }

  private decomposeToken(token: string | null) {
    if (token && token.split(".").length === 3) {
      let base64Url = token.split(".")[1];
      let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");

      try {
        let decoded = new Buffer(base64, "base64").toString();
        return JSON.parse(decoded);
      } catch (error) {
        return null;
      }
    } else {
      return null;
    }
  }

  setInitialUrl(url: string) {
    this.initialUrl = url;
  }

  setToken(response?: any, redirect?: string) {
    // access token handling
    let accessToken = response && response[this.config.responseTokenProp];
    let tokenToStore: any;

    if (accessToken) {
      if (isObject(accessToken) && isObject(accessToken.data)) {
        response = accessToken;
      } else if (isString(accessToken)) {
        tokenToStore = accessToken;
      }
    }

    if (!tokenToStore && response) {
      if (this.config.tokenRoot) {
        let tokenRoot: string = this.config.tokenRoot!;
        tokenToStore = response[tokenRoot]
          ? response[tokenRoot][this.config.tokenName]
          : response[this.config.tokenName];
      } else {
        tokenToStore = response[this.config.tokenName];
      }
    }

    if (tokenToStore) {
      this.storeToken(tokenToStore);
    }

    if (this.config.loginRedirect && !redirect) {
      window.location.href = this.getLoginRedirect();
    } else if (redirect && isString(redirect)) {
      window.location.href = encodeURI(redirect);
    }
  }

  removeToken() {
    this.decomposedToken = undefined;
    this.storage.remove(this.tokenName);
  }

  private storeToken(tokenToStore: any) {
    this.decomposedToken = undefined;
    this.storage.set(this.tokenName, tokenToStore);
  }

  isAuthenticated() {
    let tokenPayload = this.getTokenPayload();
    if (tokenPayload == null) {
      // either there is no token; or token could not be parsed
      return false;
    }
    // check if token is expired
    let exp = tokenPayload.exp;
    if (exp) {
      return exp >= Math.round(new Date().getTime() / 1000);
    }
    return true;
  }

  logout(redirect?: string) {
    return new Promise<void>((resolve) => {
      this.decomposedToken = undefined;
      this.storage.remove(this.tokenName);

      if (!redirect) {
        if (this.config.logoutRedirect) {
          window.location.href = this.getLogoutRedirect();
        }
      } else {
        window.location.href = redirect;
      }

      resolve();
    });
  }

  get tokenInterceptor(): Interceptor {
    let self = this;
    return {
      request(request: RequestMessage) {
        if (self.isAuthenticated() && self.config.httpInterceptor) {
          let token = self.getToken();
          if (token && self.config.authHeader) {
            let headerValue = self.getTokenAuthHeaderValue();
            if (headerValue) {
              request.headers.add(self.config.authHeader, headerValue);
            }
          }
        }
        return request;
      },
      response(message: HttpResponseMessage): HttpResponseMessage {
        let token = message.headers.get(self.config.authResponseHeaderForToken);
        if (token) {
          self.storeToken(token);
        }
        return message;
      },
      responseError(error: HttpResponseMessage): HttpResponseMessage {
        if (error.statusCode == 401) {
          self.removeToken();
          self.eventAggregator.publish("auth:access-denied");
        }
        throw error;
      },
    };
  }
}
